// How the Operações screens call Power, on the `HttpClient` with the browser session cookie: an
// answer is decoded with its closed admin contract; a refusal keeps its status as a reason,
// Power's Problem `detail` and the error code and trace id its body names, never retried as a
// throw.
import { Duration, Effect, Option, Schema } from 'effect';
import { dual } from 'effect/Function';
import { FetchHttpClient, HttpClient, HttpClientRequest } from 'effect/http';
import { PowerRefusal, powerRefusalOf } from '@finnest/power-admin-contracts/failure';

/** Why an Operações call did not succeed, as the screens explain it. */
export const OperationsFailureReason = Schema.Literals([
  'unauthenticated',
  'forbidden',
  'not-found',
  'rejected',
  'unavailable',
  'invalid-reply',
  'failed',
  'timed-out',
]);
export type OperationsFailureReason = typeof OperationsFailureReason.Type;

export class OperationsRequestFailed extends Schema.TaggedError<OperationsRequestFailed>()(
  'OperationsRequestFailed',
  {
    reason: OperationsFailureReason,
    detail: Schema.Option(Schema.String),
    /** The error code and trace id the answer names, to quote to support; none without an answer. */
    refusal: Schema.NullOr(PowerRefusal),
    /** What failed underneath: the transport, body or decoding error, when there was one. */
    cause: Schema.Option(Schema.instanceOf(Error)),
  },
) {
  override get message(): string {
    return `Operations request failed: ${this.reason}`;
  }
}

/** One Operações call to this deployment's Power API. */
export type OperationsCall<A> = Effect.Effect<A, OperationsRequestFailed, HttpClient.HttpClient>;

/** One Operações call, against the Power API at the base URL the edge resolves. */
export type OperationsOperation<A> = (apiBaseUrl: string) => OperationsCall<A>;

// A read never waits longer than this.
const READ_BUDGET_SECONDS = 15;
export const READ_BUDGET = Duration.seconds(READ_BUDGET_SECONDS);
// A DLQ batch moves up to 50 events, one transaction each: it waits far longer, and its end is
// read as an unknown outcome, never as nothing done.
const WRITE_DEADLINE_SECONDS = 60;
export const WRITE_DEADLINE = Duration.seconds(WRITE_DEADLINE_SECONDS);
const HTTP_SUCCESS_FLOOR = 200;
const HTTP_SUCCESS_CEILING = 299;
const HTTP_BAD_REQUEST = 400;
const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;
const HTTP_UNAVAILABLE = 503;
const CLOSED = { onExcessProperty: 'error' } as const;

const REASON_BY_STATUS: ReadonlyMap<number, OperationsFailureReason> = new Map([
  [HTTP_BAD_REQUEST, 'rejected'],
  [HTTP_UNAUTHORIZED, 'unauthenticated'],
  [HTTP_FORBIDDEN, 'forbidden'],
  [HTTP_NOT_FOUND, 'not-found'],
  [HTTP_UNAVAILABLE, 'unavailable'],
]);

// RFC 7807 answers carry what went wrong in `detail`; the other members are not read.
const decodeProblemDetail = Schema.decodeUnknownOption(
  Schema.fromJsonString(Schema.Struct({ detail: Schema.NonEmptyString })),
);

const failed = (
  reason: OperationsFailureReason,
  cause: Option.Option<Error> = Option.none(),
): OperationsRequestFailed =>
  OperationsRequestFailed.make({ reason, detail: Option.none(), refusal: null, cause });

const failedBy =
  (reason: OperationsFailureReason) =>
  (cause: Error): OperationsRequestFailed =>
    failed(reason, Option.some(cause));

/** The error code and trace id a failed call names, to quote to support; none without an answer. */
export const failureRefusal = (failure: OperationsRequestFailed): Option.Option<PowerRefusal> =>
  Option.fromNullOr(failure.refusal);

/** The answer's text when Power answered 2xx, otherwise the reason its status names. */
export const answeredText = (
  request: HttpClientRequest.HttpClientRequest,
): OperationsCall<string> =>
  Effect.gen(function* () {
    const client = yield* HttpClient.HttpClient;
    const response = yield* client
      .execute(HttpClientRequest.acceptJson(request))
      .pipe(Effect.mapError(failedBy('failed')));
    const answered =
      response.status >= HTTP_SUCCESS_FLOOR && response.status <= HTTP_SUCCESS_CEILING;
    // A refusal's status gives the reason; a body that cannot be read only loses its words.
    const reason = answered ? 'failed' : (REASON_BY_STATUS.get(response.status) ?? 'failed');
    const text = yield* response.text.pipe(Effect.mapError(failedBy(reason)));
    if (answered) return text;
    return yield* OperationsRequestFailed.make({
      reason,
      detail: Option.map(decodeProblemDetail(text), (problem) => problem.detail),
      refusal: Option.getOrNull(powerRefusalOf(text)),
      cause: Option.none(),
    });
  }).pipe(
    Effect.provideService(FetchHttpClient.RequestInit, { credentials: 'include' }),
    Effect.provideService(HttpClient.TracerPropagationEnabled, false),
  );

/** The answer decoded, closed, with `contract`; outside it the call fails as `invalid-reply`. */
export const decoded: {
  <A>(
    contract: Schema.Codec<A, unknown>,
  ): (request: HttpClientRequest.HttpClientRequest) => OperationsCall<A>;
  <A>(
    request: HttpClientRequest.HttpClientRequest,
    contract: Schema.Codec<A, unknown>,
  ): OperationsCall<A>;
} = dual(
  2,
  <A>(
    request: HttpClientRequest.HttpClientRequest,
    contract: Schema.Codec<A, unknown>,
  ): OperationsCall<A> =>
    Effect.flatMap(answeredText(request), (text) =>
      Schema.decodeEffect(Schema.fromJsonString(contract))(text, CLOSED).pipe(
        Effect.mapError(failedBy('invalid-reply')),
      ),
    ),
);

/** `call`, ended as `timed-out` once `duration` passes. */
export const within =
  (duration: Duration.Duration) =>
  <A>(call: OperationsCall<A>): OperationsCall<A> =>
    call.pipe(Effect.timeoutOrElse({ duration, orElse: () => Effect.fail(failed('timed-out')) }));
